import MobileSurfaceSnapshotRegistration from './MobileSurfaceSnapshotRegistration'
import type { Dispatch, SetStateAction } from 'react'
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'

import noop from 'lib/function/noop'
import GeoContext from 'contexts/geoContext'
import ModalContext from 'contexts/ModalContext'
import AccountAccessModal from 'components/Account/AccountAccess/AccountAccessModal'
import { useGetChat } from 'tripPlanner/hooks/api/chat'
import type { ChatNavigationOptions } from '../hooks/useChatNavigation'
import { useChatActions } from '../contexts/ChatActionsContext'
import { useChatStreamMessages } from '../contexts/ChatStreamMessagesContext'
import { useChatStreamStatus } from 'tripPlanner/assistant/contexts/ChatStreamStatusContext'
import { DrawerAskAIProvider } from '../contexts/DrawerAskAIContext'
import { MapInteractionProvider } from '../contexts/MapInteractionContext'
import {
  DEFAULT_MAP_STYLE_MODE,
  type MapStyleMode,
} from 'tripPlanner/components/ChatMap/map3DConfig'
import { useTripPlannerEventBus } from 'tripPlanner/assistant/contexts/TripPlannerEventBus'
import { useItineraryContext } from '../contexts/ItineraryContext'
import { useTripPlannerMenuDrawer } from '../hooks/useTripPlannerMenuDrawer'
import TripChatMobileAppShell from '../mobile/TripChatMobileAppShell'
import TripChatMobileChatView from '../mobile/TripChatMobileChatView'
import TripChatMobileListView from '../mobile/TripChatMobileListView'
import TripChatMobileMapView from '../mobile/TripChatMobileMapView'
import TripChatMobileCartPanelHost from '../mobile/TripChatMobileCartPanelHost'
import DrawerContainer from '../containers/DrawerContainer'
import { useTripChatMobileOverlay } from '../mobile/TripChatMobileOverlayContext'
import { useMessageContext } from '../hooks/useMessageContext'
import { useSpoofSendGuard } from 'tripPlanner/hooks/useSpoofSendGuard'
import { useChatSessionTracking } from '../hooks/useChatSessionTracking'
import { usePromptTokenSubmission } from '../hooks/usePromptTokenSubmission'
import { useChatRouteSync } from '../hooks/useChatRouteSync'
import { useCartButtonSummary } from '../hooks/useCartButtonSummary'
import { useFetchCartPanelOffers } from '../hooks/useFetchCartPanelOffers'
import { addBookingItemIdsToSelection, type BookingSelection } from '../utils/bookingSelection'
import ChatLandingPage from '../../ChatLandingPage/ChatLandingPage'
import { useAssistantHistory } from 'tripPlanner/assistant/hooks/useAssistantHistory'
import TripPlannerChatSearchModal from '../../TripPlannerChatSearchModal'

export interface TripChatMobileSurfaceContentProps {
  chatId: number | undefined
  syncChatId: (id: number | undefined, options?: ChatNavigationOptions) => void
  isNewChatRoute: boolean
  isLandingRoute: boolean
  existingRouteChatId: number | undefined
  navigateToNewChatRoute: () => void
  isLoggedIn: boolean
  isStaff: boolean
  isDevMode: boolean
  isRewindMode: boolean
  isSpoofed: boolean
  spoofingAgentEmail?: string
  onStaffViewedChatIdChange: (chatId: number | undefined) => void
  onDevModeToggle: () => void
  onRewindModeToggle: () => void
  setBookingSelection: Dispatch<SetStateAction<BookingSelection>>
  selectedBookingItemIds?: Array<string>
  prefilledOrderChipLabels: Array<string>
  onRemovePrefilledOrderTag: (tag: string) => void
}

const DEFAULT_SELECTED_BOOKING_ITEM_IDS: Array<string> = []

export default function TripChatMobileSurfaceContent({
  chatId,
  syncChatId,
  isNewChatRoute,
  isLandingRoute,
  existingRouteChatId,
  navigateToNewChatRoute,
  isLoggedIn,
  isStaff,
  isDevMode,
  isRewindMode,
  isSpoofed,
  spoofingAgentEmail,
  onStaffViewedChatIdChange,
  onDevModeToggle,
  onRewindModeToggle,
  setBookingSelection,
  selectedBookingItemIds = DEFAULT_SELECTED_BOOKING_ITEM_IDS,
  prefilledOrderChipLabels,
  onRemovePrefilledOrderTag,
}: TripChatMobileSurfaceContentProps) {
  const showModal = useContext(ModalContext)
  const {
    cartCloseRequest,
    closeOverlay,
    drawerCloseRequest,
    menuCloseRequest,
    openOverlay,
    searchCloseRequest,
  } = useTripChatMobileOverlay()
  const eventBus = useTripPlannerEventBus()
  const { hasItineraryData, itineraryData } = useItineraryContext()
  useFetchCartPanelOffers(itineraryData, !!itineraryData)
  const cartButtonSummary = useCartButtonSummary(itineraryData, isLoggedIn)
  const {
    recentChats,
  } = useAssistantHistory({ enabled: isLoggedIn, isLoggedIn })
  const { search } = useLocation()
  const { submitMessage, resetConversation, resetStreamingState, setFollowTail } = useChatActions()
  const { messages } = useChatStreamMessages()
  const { isStreaming, loadingState } = useChatStreamStatus()
  const chatInputRef = useRef<HTMLTextAreaElement | null>(null)
  const closeSearchRef = useRef(noop)
  const hasPendingSearchCloseRef = useRef(false)
  const previousSearchCloseRequestRef = useRef(searchCloseRequest)
  const [hoveredHotelId, setHoveredHotelId] = useState<string | undefined>(undefined)
  const [mapStyleMode, setMapStyleMode] = useState<MapStyleMode>(DEFAULT_MAP_STYLE_MODE)
  const isCardHoveredRef = useRef(false)
  const isLoading = loadingState.status === 'loadingHistory' || loadingState.status === 'preparingStream'
  const trackFirstMessage = useChatSessionTracking({ chatId, existingRouteChatId })
  const { currentRegionCode } = useContext(GeoContext)
  const { data: chatData } = useGetChat(chatId, currentRegionCode)
  const hiddenFromCustomer = chatData?.hiddenFromCustomer ?? true

  const handleMarkerHover = useCallback((markerId: string | undefined) => {
    if (markerId === undefined && isCardHoveredRef.current) return
    setHoveredHotelId(markerId)
  }, [])

  const handleCardHover = useCallback((offerId: string | undefined) => {
    isCardHoveredRef.current = offerId !== undefined
    setHoveredHotelId(offerId)
  }, [])

  const mapInteractionValue = useMemo(() => ({
    onCardHover: handleCardHover,
    onMarkerHover: handleMarkerHover,
    highlightedMarkerId: hoveredHotelId,
    mapStyleMode,
    onMapStyleModeChange: setMapStyleMode,
  }), [handleCardHover, handleMarkerHover, hoveredHotelId, mapStyleMode])

  const {
    activeMultiChoiceSelection,
    mobileSelectionText,
    offerContextTag,
    handleSubmit,
    submitDirect,
    handleMultiChoiceSelectionChange,
    handleRemoveTag,
    resetSelectionState,
  } = useMessageContext({
    streamingSubmit: submitMessage,
    setFollowTail,
    setMessageInputValue: noop,
    prefilledMultiChoiceSelection: prefilledOrderChipLabels,
  })

  const { confirmOrAbort } = useSpoofSendGuard({ isSpoofed, hiddenFromCustomer, chatId })

  const handleSubmitWithTracking = useCallback(async(message: string) => {
    if (!(await confirmOrAbort())) return
    trackFirstMessage()
    return handleSubmit(message)
  }, [confirmOrAbort, handleSubmit, trackFirstMessage])

  const submitDirectWithTracking = useCallback(async(message: string) => {
    if (!(await confirmOrAbort())) return
    trackFirstMessage()
    return submitDirect(message)
  }, [confirmOrAbort, submitDirect, trackFirstMessage])

  const resetChatSpecificState = useCallback(() => {
    eventBus.dispatch({ type: 'reset_chat_state' })
    resetSelectionState()
    setHoveredHotelId(undefined)
    setMapStyleMode(DEFAULT_MAP_STYLE_MODE)
    isCardHoveredRef.current = false
  }, [eventBus, resetSelectionState])

  const resetConversationState = useCallback((options?: ChatNavigationOptions) => {
    syncChatId(undefined, { shouldUpdateUrl: options?.shouldUpdateUrl })
    resetConversation({ followTail: false })
    resetChatSpecificState()
  }, [resetChatSpecificState, resetConversation, syncChatId])

  const loadChatById = useCallback((selectedChatId: number, options?: ChatNavigationOptions) => {
    if (selectedChatId === chatId) {
      return
    }

    resetChatSpecificState()
    resetStreamingState()
    syncChatId(selectedChatId, options)
    setFollowTail(false)
  }, [chatId, resetChatSpecificState, resetStreamingState, setFollowTail, syncChatId])

  const handleStartNewChat = useCallback(() => {
    onStaffViewedChatIdChange(undefined)
    resetConversationState({ shouldUpdateUrl: false })
    navigateToNewChatRoute()
  }, [navigateToNewChatRoute, onStaffViewedChatIdChange, resetConversationState])

  const handleLogin = useCallback(() => {
    showModal(<AccountAccessModal />)
  }, [showModal])

  const handleChatSelect = useCallback((selectedChatId: number) => {
    onStaffViewedChatIdChange(undefined)
    loadChatById(selectedChatId)
  }, [loadChatById, onStaffViewedChatIdChange])

  const handleChatDeleted = useCallback((deletedChatId: number, fallbackChatId: number | undefined) => {
    if (chatId !== deletedChatId) {
      return
    }

    onStaffViewedChatIdChange(undefined)
    if (fallbackChatId) {
      loadChatById(fallbackChatId)
      return
    }

    resetConversationState()
  }, [chatId, loadChatById, onStaffViewedChatIdChange, resetConversationState])

  useEffect(() => {
    if (searchCloseRequest === previousSearchCloseRequestRef.current) return

    previousSearchCloseRequestRef.current = searchCloseRequest
    hasPendingSearchCloseRef.current = true
    closeSearchRef.current()
  }, [searchCloseRequest])

  useChatRouteSync({
    chatId,
    existingRouteChatId,
    isLandingRoute,
    isNewChatRoute,
    loadChatById,
    resetConversationState,
  })

  usePromptTokenSubmission({
    search,
    isNewChatRoute,
    chatId,
    isLoading,
    isStreaming,
    onPromptSubmit: handleSubmitWithTracking,
  })

  const handleBookingItemsAdded = useCallback((bookingItemIds: Array<string>) => {
    if (bookingItemIds.length === 0) {
      return
    }

    setBookingSelection(previous => addBookingItemIdsToSelection(previous, bookingItemIds))
  }, [setBookingSelection])

  const landingPageTags = useMemo(() => {
    return [...new Set([...prefilledOrderChipLabels, ...activeMultiChoiceSelection])]
  }, [activeMultiChoiceSelection, prefilledOrderChipLabels])

  const handleAddTags = useCallback((tags: Array<string>) => {
    const selectedTags = new Set(landingPageTags)
    const deduplicated = tags.filter(tag => !selectedTags.has(tag))
    if (deduplicated.length > 0) {
      handleMultiChoiceSelectionChange([...activeMultiChoiceSelection, ...deduplicated])
    }
  }, [activeMultiChoiceSelection, handleMultiChoiceSelectionChange, landingPageTags])

  const handleLandingPageTagRemove = useCallback((tag: string) => {
    if (prefilledOrderChipLabels.includes(tag)) {
      onRemovePrefilledOrderTag(tag)
      return
    }

    handleRemoveTag(tag)
  }, [handleRemoveTag, onRemovePrefilledOrderTag, prefilledOrderChipLabels])

  const handleMenuOpenChange = useCallback((isOpen: boolean) => {
    if (isOpen) {
      openOverlay('menu')
      return
    }

    closeOverlay('menu', {
      shouldRequestOverlayClose: false,
      shouldStepHistory: false,
    })
  }, [closeOverlay, openOverlay])

  const handleSearchChats = useCallback(() => {
    hasPendingSearchCloseRef.current = false
    openOverlay('search')

    showModal<void>(
      <TripPlannerChatSearchModal
        recentChats={recentChats}
        onChatSelect={handleChatSelect}
        onCloseReady={(closeSearch) => {
          closeSearchRef.current = closeSearch
          if (hasPendingSearchCloseRef.current) {
            closeSearch()
          }
        }}
      />,
    ).finally(() => {
      closeSearchRef.current = noop
      hasPendingSearchCloseRef.current = false
      closeOverlay('search', { shouldRequestOverlayClose: false })
    })
  }, [closeOverlay, handleChatSelect, openOverlay, recentChats, showModal])

  const onMenuOpen = useTripPlannerMenuDrawer({
    variant: 'mobile',
    isLoggedIn,
    isStaff,
    isDevMode,
    isRewindMode,
    onStartNewChat: handleStartNewChat,
    onChatSelect: handleChatSelect,
    onChatDeleted: handleChatDeleted,
    onDevModeToggle,
    onRewindModeToggle,
    onSearchChats: handleSearchChats,
    activeChatId: chatId,
    hasActiveChatItinerary: hasItineraryData,
    closeSignal: menuCloseRequest,
    onDrawerOpenChange: handleMenuOpenChange,
  })

  const handleCartOpenChange = useCallback((isOpen: boolean) => {
    if (isOpen) {
      openOverlay('cart')
      return
    }

    closeOverlay('cart', {
      shouldRequestOverlayClose: false,
      shouldStepHistory: false,
    })
  }, [closeOverlay, openOverlay])

  const handleDrawerOpenChange = useCallback((isOpen: boolean) => {
    if (isOpen) {
      openOverlay('drawer')
      return
    }

    closeOverlay('drawer', {
      shouldRequestOverlayClose: false,
      shouldStepHistory: false,
    })
  }, [closeOverlay, openOverlay])

  const hasMessages = messages.length > 0
  const shouldShowLandingPage = useMemo(() => {
    const isLandingExperience = isLandingRoute && !hasMessages
    const shouldShowNewChatComposer = isNewChatRoute && !hasMessages

    return isLandingExperience || shouldShowNewChatComposer
  }, [hasMessages, isLandingRoute, isNewChatRoute])

  const tabPanels = useMemo(() => ({
    chat: shouldShowLandingPage ? (
      <ChatLandingPage
        onSubmit={handleSubmitWithTracking}
        onSubmitDirect={submitDirectWithTracking}
        onAddTags={handleAddTags}
        disabled={isLoading || isStreaming}
        selectedTags={landingPageTags}
        onRemoveTag={handleLandingPageTagRemove}
        onBookingSelectionChange={setBookingSelection}
        onBookingItemsAdded={handleBookingItemsAdded}
      />
    ) : (
      <TripChatMobileChatView
        chatId={chatId}
        isLoggedIn={isLoggedIn}
        isDevMode={isDevMode}
        isSpoofed={isSpoofed}
        hiddenFromCustomer={hiddenFromCustomer}
        spoofingAgentEmail={spoofingAgentEmail}
        navigateToNewChatRoute={navigateToNewChatRoute}
        selectedBookingItemIds={selectedBookingItemIds}
        onBookingItemsAdded={handleBookingItemsAdded}
        handleSubmit={handleSubmitWithTracking}
        handleMultiChoiceSelectionChange={handleMultiChoiceSelectionChange}
        activeMultiChoiceSelection={activeMultiChoiceSelection}
        prefilledSelectionTags={prefilledOrderChipLabels}
        mobileSelectionText={mobileSelectionText}
        offerContextTag={offerContextTag}
        handleRemoveTag={handleRemoveTag}
        onRemovePrefilledSelectionTag={onRemovePrefilledOrderTag}
        chatInputRef={chatInputRef}
      />
    ),
    list: <TripChatMobileListView />,
    map: <TripChatMobileMapView />,
  }), [
    activeMultiChoiceSelection,
    chatId,
    handleAddTags,
    handleBookingItemsAdded,
    handleLandingPageTagRemove,
    handleMultiChoiceSelectionChange,
    handleRemoveTag,
    handleSubmitWithTracking,
    isDevMode,
    isLoading,
    isLoggedIn,
    isSpoofed,
    hiddenFromCustomer,
    isStreaming,
    landingPageTags,
    mobileSelectionText,
    navigateToNewChatRoute,
    offerContextTag,
    onRemovePrefilledOrderTag,
    prefilledOrderChipLabels,
    selectedBookingItemIds,
    setBookingSelection,
    shouldShowLandingPage,
    spoofingAgentEmail,
    submitDirectWithTracking,
  ])

  return (
    <MapInteractionProvider value={mapInteractionValue}>
      <DrawerAskAIProvider
        submitMessage={submitMessage}
        messages={messages}
        isStreaming={isStreaming}
      >
        <TripChatMobileAppShell
          onMenuOpen={onMenuOpen}
          tabPanels={tabPanels}
          showTabs={!shouldShowLandingPage}
          showComposerReservedSpace={false}
          showCartButton={!!itineraryData}
          showDownloadButton={hasItineraryData}
          cartItemCount={cartButtonSummary.bookableItemCount}
          cartTotalPrice={cartButtonSummary.estimatedTotal}
          isCartButtonLoading={cartButtonSummary.isLoading}
          isSpoofed={isSpoofed}
          hiddenFromCustomer={hiddenFromCustomer}
          chatExists={chatId !== undefined}
          chatId={chatId}
          onCreateNewChat={handleStartNewChat}
          onLogin={!isLoggedIn && shouldShowLandingPage ? handleLogin : undefined}
        >
          <MobileSurfaceSnapshotRegistration showTabs={!shouldShowLandingPage} />
          <DrawerContainer
            closeSignal={drawerCloseRequest}
            onDrawerOpenChange={handleDrawerOpenChange}
            keepOpenAfterItineraryAdd
          />
          <TripChatMobileCartPanelHost
            closeSignal={cartCloseRequest}
            onCartOpenChange={handleCartOpenChange}
          />
        </TripChatMobileAppShell>
      </DrawerAskAIProvider>
    </MapInteractionProvider>
  )
}
